import { Text, Message, useIsDesignLanguage, DesignLanguage } from '@afterpaytouch/core'
import { ModuleContainerProps, TopazErrorContext } from '..'
import { FormBuilderRef, Schema } from '@afterpaytouch/form'
import React, { useEffect, useMemo, useRef, useState } from 'react'
import {
  useConsumerSelector,
  useHasSavedPaymentMethods,
  useLastPaymentDate,
  useSelectedPaymentMethod,
  useCountry,
  useExperiment,
  ConsumerProfile,
  useAppDispatch,
  useIsRecoverableFlow,
  selectRecoverableDeclinesPaymentMethod,
  useLastSuccessfulSetup,
  useTraceId,
  useTopazState,
  useIsSingleUseCard,
  saveNewPaymentMethodCard,
  selectPurchaseMethod,
  useIsAfterpayFinanceFeeEligible,
} from '../../state'
import { getAddressPreview, getCardDetailsFromState, getInitialAddressDetails, getSchemaAndFormData } from './utils'
import { NewPaymentMethodFormSchema, SchemaUpdates } from './types'
import { useTranslation } from '../../utils/convergence'
import { useCheckoutSizes } from '../../utils/responsive'
import { CardFlowType, CardBrand, Feature, PaymentMethodType, ResultCode } from '@afterpay/types'
import { TrackingEvent } from '../../model/monitoring'
import { useMonitoringWithEnduringEventProperties } from '../../integrations/monitoring'
import { ApiErrors, ErrorMessage } from './ErrorMessage'
import { Features, NewCard, OverdueType, SelectedPaymentMethod, TopazStatus } from '../../model'
import { compareObjects } from '@afterpay/utils'
import { useFlag } from '@afterpaytouch/feature'
import { parseTopazLogEventProperties } from '../../utils/topaz'
import { BlockedReasonCode } from '@afterpaytouch/topaz-api'

export const useModuleContainerProps = (): ModuleContainerProps => {
  const isArcadeDesignLanguage = useIsDesignLanguage(DesignLanguage.Arcade)

  if (isArcadeDesignLanguage) {
    return {
      nested: true,
      padding: 'None',
    }
  }

  return {
    nested: true,
    padding: { top: 'Large', bottom: 'Small', horizontal: 'XLarge' },
  }
}

interface UsePaymentMethodFormStateProps {
  showApplePay: boolean
  displayCreditCardMessage: boolean
  feature: Feature
  enabledCardTypes: CardBrand[]
  errors: ApiErrors
}

interface UsePaymentMethodFormStateReturn {
  formRef: React.RefObject<FormBuilderRef>
  schema: Schema<NewPaymentMethodFormSchema>
  formData: NewPaymentMethodFormSchema
  handleOnChange: (data: NewPaymentMethodFormSchema) => void
  setIsTopazError: (isTopazError: boolean) => void
  setDisplayErrors: (displayErrors: boolean) => void
  setSubmitted: (submitted: boolean) => void
  submitted: boolean
}

export const useBuildCardForm = ({
  showApplePay,
  displayCreditCardMessage,
  feature,
  enabledCardTypes,
  errors,
}: UsePaymentMethodFormStateProps): UsePaymentMethodFormStateReturn => {
  const formRef = useRef<FormBuilderRef>(null)
  const isArcadeDesignLanguage = useIsDesignLanguage(DesignLanguage.Arcade)
  const [displayErrors, setDisplayErrors] = useState<boolean>(false)
  const { textSize } = useCheckoutSizes()
  const defaultCountry = useCountry()
  const { t } = useTranslation()
  const [isTopazError, setIsTopazError] = useState<boolean>(false)
  const { profile, accountCountry = defaultCountry } = useConsumerSelector()
  const [submitted, setSubmitted] = useState(false)
  const { control: isCardHolderNameField } = useExperiment('CheckoutHideCardHolderName')
  const [schemaUpdates, setSchemaUpdates] = useState<SchemaUpdates>({
    billingCheckboxTicked: true,
    isTopazError: false,
    displayErrors: false,
    showCardFieldHeading: !showApplePay,
    showCreditCardMessage: displayCreditCardMessage,
    showCardHolderNameField: isCardHolderNameField,
  })
  const topazSelectedPaymentMethod = useSelectedPaymentMethod()
  const hasSavedPaymentMethods = useHasSavedPaymentMethods()
  const lastPaymentDate = useLastPaymentDate()
  const handleOnChange = (data: NewPaymentMethodFormSchema): void => {
    const { billingAndProfileAddressEqual } = data
    setIsTopazError(false)
    setSchemaUpdates((currSchema) => ({
      ...currSchema,
      ...(typeof billingAndProfileAddressEqual !== 'undefined' && { billingCheckboxTicked: billingAndProfileAddressEqual }),
    }))

    setFormData(data)
  }
  const { schema, initialFormData } = useMemo(() => {
    const { contactAddress } = profile ?? {}
    const initialAddressDetails = getInitialAddressDetails(contactAddress)
    const addressPreviewString = getAddressPreview(initialAddressDetails)

    const components = {
      addressPreview: () => (
        <Text size={textSize} color={'Gray40'}>
          {addressPreviewString}
        </Text>
      ),
      errors: () => <ErrorMessage errors={errors} displayErrors={displayErrors} textSize={textSize} />,
      creditCardMessage: () => (
        <Message
          kind='Basic'
          iconName='Info'
          heading={t('paymentMethod:creditCardMessage.heading')}
          description={t('paymentMethod:creditCardMessage.message')}
        />
      ),
    }
    const initialCardDetails = getCardDetailsFromState(topazSelectedPaymentMethod, profile as ConsumerProfile, hasSavedPaymentMethods)
    return getSchemaAndFormData(
      components,
      t,
      initialCardDetails,
      initialAddressDetails,
      accountCountry,
      enabledCardTypes,
      lastPaymentDate,
      schemaUpdates,
      feature,
      {},
      isArcadeDesignLanguage
    )
  }, [
    accountCountry,
    enabledCardTypes,
    hasSavedPaymentMethods,
    lastPaymentDate,
    profile,
    schemaUpdates,
    topazSelectedPaymentMethod,
    textSize,
    submitted,
    feature,
  ])

  const [formData, setFormData] = useState(initialFormData)
  const [isBillingAddressDetailsChanged, setIsBillingAddressDetailsChanged] = useState<boolean>(false)
  const { logEvent } = useMonitoringWithEnduringEventProperties()

  useEffect(() => {
    setSchemaUpdates((currSchema) => ({ ...currSchema, isTopazError, displayErrors }))
  }, [isTopazError, displayErrors])

  useEffect(() => {
    if (
      typeof formData?.address !== 'undefined' &&
      !(
        formData?.address?.address1 === profile?.contactAddress?.address1 &&
        formData?.address?.address2 === profile?.contactAddress?.address2 &&
        formData?.address?.postcode === profile?.contactAddress?.postcode &&
        formData?.address?.state === profile?.contactAddress?.state &&
        formData?.address?.suburb === profile?.contactAddress?.suburb
      ) &&
      !isBillingAddressDetailsChanged
    ) {
      setIsBillingAddressDetailsChanged(true)
      logEvent(TrackingEvent.CHANGED_ADD_NEW_PAYMENT_METHOD_BILLING_ADDRESS)
    }
  }, [
    formData?.address,
    isBillingAddressDetailsChanged,
    logEvent,
    profile?.contactAddress?.address1,
    profile?.contactAddress?.address2,
    profile?.contactAddress?.postcode,
    profile?.contactAddress?.state,
    profile?.contactAddress?.suburb,
  ])

  return {
    schema,
    formData,
    handleOnChange,
    formRef,
    setIsTopazError,
    setDisplayErrors,
    submitted,
    setSubmitted,
  }
}

type CardPaymentHandler = (payload: NewCard) => Promise<void>
type ApplePayHandler = () => Promise<void>

interface UseHandleCardPaymentMethodArgs {
  formRef: React.RefObject<FormBuilderRef | null>
  setSubmitted: React.Dispatch<React.SetStateAction<boolean>>
  onSuccess: () => void
}

export const useHandleCardPayment = ({ formRef, onSuccess, setSubmitted }: UseHandleCardPaymentMethodArgs): CardPaymentHandler => {
  const { logEvent } = useMonitoringWithEnduringEventProperties()
  const dispatch = useAppDispatch()
  const isThreeDSEnabled = useFlag(Features.CHECKOUT_THREE_DS_ENABLED)
  const isRecoverableFlow = useIsRecoverableFlow()
  const isSupFlow = useIsSingleUseCard()
  const isAFF = useIsAfterpayFinanceFeeEligible()
  const checkoutSaveNewCardEnabled = useFlag(Features.CHECKOUT_SAVE_NEW_CARD)
  const shouldSaveNewCardPayment = checkoutSaveNewCardEnabled && isSupFlow
  const lastSuccessfulSetup = useLastSuccessfulSetup()
  const standardCheckoutTraceId = useTraceId()
  const { purchase } = useTopazState()
  const traceId = isRecoverableFlow ? lastSuccessfulSetup?.traceId : standardCheckoutTraceId
  const cardPaymentHandler = async (payload: NewCard): Promise<void> => {
    if (formRef.current === null) {
      return
    }
    const isValid: boolean = await formRef.current?.triggerValidation()
    if (!isValid) {
      return
    }
    setSubmitted(true)
    try {
      const paymentMethod: SelectedPaymentMethod = {
        card: payload,
        type: PaymentMethodType.CREDIT_CARD,
      }

      const exactSameDetails = compareObjects(lastSuccessfulSetup, { paymentMethod, traceId })
      if (exactSameDetails) {
        onSuccess()
        return
      }
      // TODO: recoverable flow
      if (isRecoverableFlow) {
        const resultCode = await dispatch(selectRecoverableDeclinesPaymentMethod(paymentMethod, true, OverdueType.NewPayment, isThreeDSEnabled))
        if (resultCode !== ResultCode.OK) {
          logEvent(
            TrackingEvent.CHECKOUT_TOPAZ_ERROR,
            parseTopazLogEventProperties({
              resultCode,
              errorMessage: purchase.topazApiErrorMessage,
              context: TopazErrorContext.recoverableFlow,
            })
          )
        }
      } else if (shouldSaveNewCardPayment && !isThreeDSEnabled) {
        const resultCode = await dispatch(saveNewPaymentMethodCard(paymentMethod, isAFF ? CardFlowType.AFF : CardFlowType.DEFAULT))
        if (resultCode !== ResultCode.OK) {
          logEvent(
            TrackingEvent.CHECKOUT_TOPAZ_ERROR,
            parseTopazLogEventProperties({ resultCode, errorMessage: purchase.topazApiErrorMessage, context: TopazErrorContext.saveNewCardPaymentMethodSUP })
          )
        }
      } else {
        const result = await dispatch(selectPurchaseMethod(paymentMethod))
        if (paymentMethod.type === PaymentMethodType.CREDIT_CARD && result.resultCode !== ResultCode.OK) {
          const isIssuerRestricted =
            [BlockedReasonCode.ISSUER_RESTRICTED, BlockedReasonCode.CAPITAL_ONE_ISSUER_RESTRICTED].includes(result.blockedReasonCode as BlockedReasonCode) &&
            result.resultCode === ResultCode.UNACCEPTABLE_CARD
          if (isIssuerRestricted) {
            logEvent(
              TrackingEvent.CHECKOUT_TOPAZ_ERROR,
              parseTopazLogEventProperties({
                topazStatus: TopazStatus[TopazStatus.UnacceptableCardIssuerRestricted],
                resultCode: result.resultCode,
                errorMessage: purchase.topazApiErrorMessage,
                context: TopazErrorContext.saveNewCardPaymentMethod,
              })
            )
          } else {
            logEvent(
              TrackingEvent.CHECKOUT_TOPAZ_ERROR,
              parseTopazLogEventProperties({
                resultCode: result.resultCode,
                errorMessage: purchase.topazApiErrorMessage,
                context: TopazErrorContext.saveNewCardPaymentMethod,
              })
            )
          }
        }
      }
    } catch (e) {
      if (purchase.status > TopazStatus.Saved) {
        if (isRecoverableFlow) {
          // No dealing with state trace ID when setting new cards
          // So the only errors will come from credit card service
          logEvent(TrackingEvent.VIEWED_NON_TERMINAL_ERROR, {
            type: 'exception',
            errorCode: e?.data?.errorCode,
            errorMessage: e?.data?.message,
          })
        } else {
          logEvent(
            TrackingEvent.CHECKOUT_TOPAZ_ERROR,
            parseTopazLogEventProperties({
              type: 'exception',
              errorCode: e?.errorCode ?? e.code,
              errorMessage: e?.message ?? e.name,
              topazStatus: TopazStatus[purchase.status],
            })
          )
        }
      }
    }
  }
  return cardPaymentHandler
}

type UseHandleApplePayArgs = Omit<UseHandleCardPaymentMethodArgs, 'formRef'>

export const useHandleApplePay = ({ setSubmitted, onSuccess }: UseHandleApplePayArgs): ApplePayHandler => {
  const isRecoverableFlow = useIsRecoverableFlow()
  const dispatch = useAppDispatch()

  const handler = async (): Promise<void> => {
    setSubmitted(true)
    if (isRecoverableFlow) {
      await dispatch(selectRecoverableDeclinesPaymentMethod({ type: PaymentMethodType.APPLE_PAY }))
    } else {
      await dispatch(selectPurchaseMethod({ type: PaymentMethodType.APPLE_PAY }))
    }
    onSuccess()
  }
  return handler
}
